import { DomainList, DomainListItemCard } from "@/components/domain";
import { Badge } from "@/components/ui/badge";
import { useDeleteWorker, useGetWorkers } from "@/hooks/workers";
import { T } from "@/i18n";
import { formatPhoneNumber } from "@/lib/utils";
import { Worker } from "@/models";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { HardHat } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/(workers)/workers")({
  component: RouteComponent,
});

function RouteComponent() {
  const query = useGetWorkers();

  return (
    <DomainList
      domain="workers"
      breadcrumbs={[
        {
          content: (
            <>
              <T tKey="workers.title" /> <HardHat width={16} height={16} />
            </>
          ),
          href: "/workers",
        },
      ]}
      tableOptions={{
        hasActions: true,
        columns: [
          {
            header: "workers.fields.name",
          },
          {
            header: "workers.fields.phone",
          },
          {
            header: "workers.fields.email",
          },
          {
            header: "workers.fields.birthdate",
          },
          {
            header: "workers.fields.country",
          },
          {
            header: "workers.fields.language",
          },
        ],
      }}
      type="table"
      data={query.data}
      isLoading={query.isLoading}
      newLink="/workers/new"
      renderItem={(worker, index, isTable) => (
        <WorkerCard worker={worker} key={index} isTable={isTable} />
      )}
    />
  );
}

function WorkerCard({ worker, isTable }: { worker: Worker; isTable: boolean }) {
  const { mutateAsync } = useDeleteWorker();
  const deleteWorker = () => mutateAsync(worker);
  const { t } = useTranslation();
  return (
    <DomainListItemCard
      table={isTable}
      domain="workers"
      title={worker.name}
      titleTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      description={formatPhoneNumber(worker.phone)}
      descriptionTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      viewLink={(children, key) => (
        <Link key={key} to="/workers/$id" params={{ id: worker.id.toString() }}>
          {children}
        </Link>
      )}
      editLink={(children, key) => (
        <Link
          key={key}
          to="/workers/$id/edit"
          params={{ id: worker.id.toString() }}
        >
          {children}
        </Link>
      )}
      contentMap={[
        {
          value: worker.email,
        },
        {
          label: t("workers.fields.birthdate"),
          value: worker.birthdate
            ? format(new Date(worker.birthdate), "PPP")
            : "-",
        },
        {
          label: t("workers.fields.country"),
          value: worker.country ? (
            <T tKey={`countries.${worker.country}`} />
          ) : (
            "-"
          ),
        },
        {
          label: t("workers.fields.language"),
          tableClassName: "min-w-48",
          value: !worker.language?.length ? (
            "-"
          ) : (
            <div className="flex flex-wrap items-center gap-1">
              {worker.language?.map((l) => (
                <Badge className="whitespace-nowrap" key={l}>
                  {l}
                </Badge>
              ))}
            </div>
          ),
        },
      ]}
      onDelete={deleteWorker}
    />
  );
}
